'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useStickyFooter } from '@/app_lib/providers/StickyFooterProvider';
import { AdTags } from '@/app_lib/services/ads/defaults';
import type { AdTag } from '@/app_lib/types/Article';
import { AnalyticsEvent } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

export interface StnVideoProps {
  dataCy?: string;
  videoId: string;
  adTags?: AdTag[];
}

// TODO: a lot of this logic is specific to the article STN Video component, that logic should be moved to a separate component
export const StnVideo = ({ videoId, adTags, dataCy = '' }: StnVideoProps) => {
  const [apiReady, setApiReady] = useState(false);
  const [paywallHeight, setPaywallHeight] = useState(0);
  const [shouldPaywallFloat, setShouldPaywallFloat] = useState(true);
  const updateStnCid = adTags?.find((tag) => tag.node.name === AdTags.NoVideoAds || tag.node.name === AdTags.NoAds);
  const updatedVideoId = updateStnCid ? videoId.replace(/-14497$/g, '-16481') : videoId;

  const { renderStickyFooter } = useStickyFooter();

  useEffect(() => {
    window.setupS2NApi = (getS2nApiForPlayer: (player: Element) => void) => {
      const s2nPlayer = document.querySelector('.s2nPlayer');
      if (s2nPlayer) {
        window.s2nApi = getS2nApiForPlayer(s2nPlayer) as unknown as { float: boolean; floatVerticalTranslate: number };
        setApiReady(true);
      }
    };

    return () => {
      delete window.setupS2NApi;
    };
  }, []);

  useEffect(() => {
    const paywallResizeObserver = new ResizeObserver((entries) => {
      setPaywallHeight(entries[0].contentRect.height);
    });

    const paywallElement = document.querySelector('.paywallax-ii-selector');
    if (paywallElement) {
      paywallResizeObserver.observe(paywallElement);
    }

    return () => paywallResizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const pianoModalObserver = new MutationObserver((records) => {
      const modalWasAdded = records.some((mutation) => {
        const currentRecordAddedModal = Array.from(mutation.addedNodes).some((added) => {
          return (added as HTMLElement)?.classList?.contains('tp-modal');
        });

        return currentRecordAddedModal;
      });

      if (modalWasAdded) {
        setShouldPaywallFloat(false);
        return;
      }

      const modalWasRemoved = records.some((mutation) => {
        const currentRecordRemovedModal = Array.from(mutation.removedNodes).some((removed) => {
          return (removed as HTMLElement)?.classList?.contains('tp-modal');
        });

        return currentRecordRemovedModal;
      });

      if (modalWasRemoved) {
        setShouldPaywallFloat(true);
        return;
      }
    });

    pianoModalObserver.observe(document.body, { childList: true });

    return () => pianoModalObserver.disconnect();
  }, []);

  useEffect(() => {
    if (apiReady) {
      const stickyFooterRendered = paywallHeight === 0 && renderStickyFooter;
      window.s2nApi.floatVerticalTranslate = paywallHeight;
      window.s2nApi.float = stickyFooterRendered ? false : shouldPaywallFloat;
    }
  }, [apiReady, paywallHeight, shouldPaywallFloat, renderStickyFooter]);

  return (
    <>
      <Script
        aria-label='Featured Video'
        src={`https://embed.sendtonews.com/player3/embedcode.js?SC=${updatedVideoId}`}
        strategy='afterInteractive'
      />
      <div
        className={cn(`stn-video-player s2nPlayer k-${updatedVideoId}`, AnalyticsEvent.ImpressionElement)}
        data-content-placement='stn-video'
        data-cy={dataCy}
        data-s2n-api='setupS2NApi'
        data-type='float'
      />
    </>
  );
};
